"use client";
import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ProductContext } from '../context/ProductContext';
import { useCategories } from '../context/CategoriesContext';
import Cat from '../Products/Cat';
import PaginatedProductGrid from './PaginatedProductGrid';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';

function FilteredProducts() {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchProductsByCategory } = useContext(ProductContext);
  const { categories, loading: categoriesLoading } = useCategories();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Filter states
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(true);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('name-asc'); // name-asc, name-desc, price-asc, price-desc

  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';

  // Find selected category
  useEffect(() => {
    if (categories.length > 0 && categoryId) {
      const category = categories.find(cat => cat.id === categoryId);
      setSelectedCategory(category);
    }
  }, [categories, categoryId]);

  // Fetch products for selected category
  const fetchCategoryProducts = useCallback(async (catId) => {
    if (!catId) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching products for category: ${catId}`);
      const categoryProducts = await fetchProductsByCategory(catId);
      setProducts(categoryProducts || []);
      console.log(`Successfully fetched ${categoryProducts?.length || 0} products for category ${catId}`);
    } catch (err) {
      console.error(`Error fetching products for category ${catId}:`, err);
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [fetchProductsByCategory]);

  // Fetch products when category changes
  useEffect(() => {
    if (categoryId) {
      fetchCategoryProducts(categoryId);
    }
  }, [categoryId, fetchCategoryProducts]);

  // Apply search and price filtering
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply price filter
    if (priceRange.min || priceRange.max) {
      filtered = filtered.filter(product => {
        const price = Number(product.offerPrice) || Number(product.price) || 0;
        const min = priceRange.min ? Number(priceRange.min) : 0;
        const max = priceRange.max ? Number(priceRange.max) : Infinity;
        return price >= min && price <= max;
      });
    }

    return filtered;
  }, [products, searchQuery, priceRange]);

  // Apply sorting
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];

    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'price-asc':
        return sorted.sort((a, b) => {
          const priceA = Number(a.offerPrice) || Number(a.price) || 0;
          const priceB = Number(b.offerPrice) || Number(b.price) || 0;
          return priceA - priceB;
        });
      case 'price-desc':
        return sorted.sort((a, b) => {
          const priceA = Number(a.offerPrice) || Number(a.price) || 0;
          const priceB = Number(b.offerPrice) || Number(b.price) || 0;
          return priceB - priceA;
        });
      default:
        return sorted;
    }
  }, [filteredProducts, sortBy]);

  // Handle category selection
  const handleCategorySelect = (catId) => {
    setShowCategoryDropdown(false);
    if (catId) {
      navigate(`/products/category/${catId}`);
    } else {
      navigate('/products');
    }
  };

  // Handle sort selection
  const handleSortSelect = (sort) => {
    setSortBy(sort);
    setShowSortDropdown(false);
  };

  // Get sort label
  const getSortLabel = () => {
    switch (sortBy) {
      case 'name-asc': return 'Name: A-Z';
      case 'name-desc': return 'Name: Z-A';
      case 'price-asc': return 'Price: Low to High';
      case 'price-desc': return 'Price: High to Low';
      default: return 'Sort By';
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryId]);

  if (categoriesLoading) {
    return (
      <div className="flex overflow-hidden flex-col bg-orange-50 bg-opacity-90">
        <Cat/>
        <main className="flex flex-col items-center w-full">
          <div className="mt-10 text-center text-gray-600" role="status" aria-live="polite">
            <div className="inline-flex items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600" aria-hidden="true"></div>
              Loading categories...
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden flex-col bg-orange-50 bg-opacity-90">
      <Cat/>

      <main className="flex flex-col items-center w-full">
        {/* Title Section - Same as original Products page */}
        <h1
          className="mt-5 text-6xl font-semibold text-stone-900 max-md:text-4xl text-center"
          style={{ fontFamily: 'Gloock, serif' }}
        >
          {selectedCategory ? selectedCategory.title : 'Browse Products'}
        </h1>

        {selectedCategory?.subtitle && (
          <p className="text-md max-w-4xl px-3 md:px-0 mx-auto text-gray-600 max-md:text-base text-center mt-3">
            {selectedCategory.subtitle}
          </p>
        )}

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mt-6 max-w-4xl mx-auto px-4">
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-teal-800 text-sm text-center">
                Showing results for "<strong>{searchQuery}</strong>" in {selectedCategory?.title || 'all categories'}
              </p>
            </div>
          </div>
        )}

        {/* Main Content Layout - Sidebar + Products */}
        <div className="w-full max-w-[1440px] mx-auto mt-8 flex gap-6 px-4">
          {/* Left Sidebar - Filters (Sticky) */}
          <aside className="w-80 flex-shrink-0 mb-4 max-lg:w-72 max-md:hidden">
            <div className="sticky top-24 bg-white rounded-none shadow-sm border border-orange-200 overflow-hidden">
              {/* Filter Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-stone-50 to-gray-50">
                <h2 className="text-xl font-semibold text-stone-900 flex items-center gap-2">
                  <SlidersHorizontal size={20} />
                  Filters
                </h2>
              </div>

              <div className="p-6 space-y-6">
                {/* Category Filter */}
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-3">
                    Category
                  </label>
                  <div>
                    <button
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                      className="w-full group flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg hover:border-orange-400 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {selectedCategory?.image && (
                          <img
                            src={selectedCategory.image}
                            alt={selectedCategory.title}
                            className="w-6 h-6 object-cover rounded-full"
                          />
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {selectedCategory ? selectedCategory.title : 'All Categories'}
                        </span>
                      </div>
                      <ChevronDown size={16} className={`text-gray-500 group-hover:text-orange-600 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showCategoryDropdown && (
                      <div className="mt-3 space-y-2 max-h-84 overflow-y-auto custom-scrollbar">
                        <button
                          onClick={() => handleCategorySelect(null)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 border ${
                            !selectedCategory ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <SlidersHorizontal size={14} className="text-white" />
                          </div>
                          <span className="font-medium">All Categories</span>
                        </button>
                        {categories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => handleCategorySelect(category.id)}
                            className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 border ${
                              selectedCategory?.id === category.id
                                ? 'bg-teal-50 border-teal-200 text-teal-700'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="w-8 h-8 flex-shrink-0">
                              {category.image ? (
                                <img
                                  src={category.image}
                                  alt={category.title}
                                  className="w-full h-full object-cover rounded-full"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-teal-400 to-green-600 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs font-bold">
                                    {category.title.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{category.title}</div>
                              {category.subtitle && (
                                <div className="text-xs text-gray-500 truncate">
                                  {category.subtitle}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Price Range Filter */}
                <div>
                  <label className="block text-md font-medium text-gray-700 mb-3">
                    Price Range
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <input
                          type="number"
                          placeholder="Min Price"
                          value={priceRange.min}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <span className="text-gray-400 font-medium">to</span>
                      <div className="flex-1">
                        <input
                          type="number"
                          placeholder="Max Price"
                          value={priceRange.max}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    {(priceRange.min || priceRange.max) && (
                      <button
                        onClick={() => setPriceRange({ min: '', max: '' })}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Clear Price Filter
                      </button>
                    )}
                  </div>
                </div>

                {/* Active Filters Summary */}
                {(priceRange.min || priceRange.max) && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Active Filters</h3>
                    <div className="space-y-2">
                      {(priceRange.min || priceRange.max) && (
                        <div className="flex items-center justify-between bg-teal-50 px-3 py-2 rounded-lg border border-teal-200">
                          <span className="text-sm text-teal-800">
                            Price: ₹{priceRange.min || '0'} - ₹{priceRange.max || '∞'}
                          </span>
                          <button
                            onClick={() => setPriceRange({ min: '', max: '' })}
                            className="text-teal-600 hover:text-teal-700"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Right Content - Products */}
          <div className="flex-1 min-w-0">
            {/* Sort and Results Header */}
            <div className="flex items-center justify-between mb-6 bg-white rounded-lg p-4 shadow-sm border border-orange-200">
              <div className="text-sm text-gray-600">
                {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''} found
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-300 group rounded-lg hover:border-orange-400 transition-colors min-w-[160px] justify-between"
                >
                  <span className="text-sm font-medium">{getSortLabel()}</span>
                  <ChevronDown size={16} className={`transition-transform group-hover:text-orange-600 ${showSortDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {[
                      { value: 'name-asc', label: 'Name: A-Z' },
                      { value: 'name-desc', label: 'Name: Z-A' },
                      { value: 'price-asc', label: 'Price: Low to High' },
                      { value: 'price-desc', label: 'Price: High to Low' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortSelect(option.value)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-sm ${
                          sortBy === option.value ? 'bg-teal-100 text-teal-800 font-medium' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Products Grid with Pagination */}
            {error ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="text-red-600 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium mb-2">Failed to Load Products</h3>
                  <p className="text-sm text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={() => fetchCategoryProducts(categoryId)}
                    className="bg-stone-900 text-white px-6 py-2 rounded-lg hover:bg-stone-800 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <PaginatedProductGrid
                products={sortedProducts}
                loading={loading}
                selectedCategory={selectedCategory}
                searchQuery={searchQuery}
                productsPerPage={15}
              />
            )}
          </div>
        </div>
      </main>

      {/* Click outside handlers */}
      {showSortDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowSortDropdown(false);
          }}
        />
      )}
    </div>
  );
}

export default FilteredProducts;

// Custom scrollbar styles
const customScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    transition: background-color 0.2s ease;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = customScrollbarStyles;
  if (!document.head.querySelector('style[data-custom-scrollbar]')) {
    styleElement.setAttribute('data-custom-scrollbar', 'true');
    document.head.appendChild(styleElement);
  }
}
